import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useFadeIn } from '../hooks/useFadeIn';
const PREVIEW_PAGES = [{
  src: 'https://cdn.magicpatterns.com/uploads/gGfr5nTbgHsTXGAqySHxLD/Untitled_design_(4).jpg',
  alt: 'El Problema con los Espejos que Usamos — Tomo 1'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/neFf1QK8ih84so65v5J1jW/Untitled_design_(7).jpg',
  alt: 'El Mapa de tus Emociones Intensas — Tomo 2'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/cK5M7F3jL7bREBUVT5Kp7d/Untitled_design_(10).jpg',
  alt: 'Las 5 Señales de Que el Estrés te Está Ganando — Tomo 3'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/twZyM6aANNPB4aQQXBEcV1/Untitled_design_(5).jpg',
  alt: 'Autocrítica Sana vs. Autocrítica Insana — Tomo 5'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/7BMkP1tDHrrmgqmFDbaPaD/Untitled_design_(11).jpg',
  alt: 'Los 6 Tipos de Límites en las Relaciones — Tomo 5'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/1KgErqoeonkbDQQCNqFAvq/Untitled_design_(8).jpg',
  alt: 'Zona Roja: Protocolo de Emergencia Emocional — Tomo 6'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/djn24Xc4YE4cF87ppm3meX/Untitled_design_(6).jpg',
  alt: 'El Mapa de tus Emociones — Tomo 2'
}];
export function PreviewCarousel() {
  const fadeIn = useFadeIn();
  const [current, setCurrent] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const currentRef = useRef(0);
  const total = PREVIEW_PAGES.length;
  const goTo = useCallback((index: number) => {
    const next = (index % total + total) % total;
    setCurrent(next);
    currentRef.current = next;
    if (scrollRef.current) {
      const child = scrollRef.current.children[next] as HTMLElement;
      if (child) {
        scrollRef.current.scrollTo({
          left: child.offsetLeft - scrollRef.current.offsetLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [total]);
  const prev = () => goTo(current - 1);
  const next = () => goTo(current + 1);
  // Sync dot indicator with scroll position — stable listener, no re-registration
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollLeft = container.scrollLeft;
          const width = container.offsetWidth;
          const index = Math.round(scrollLeft / width);
          if (index !== currentRef.current && index >= 0 && index < total) {
            currentRef.current = index;
            setCurrent(index);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    container.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [total]);
  return <section className="py-10 md:py-16 px-4 bg-white" style={{
    contain: 'layout style'
  }} data-id="element-170">
      <div className="max-w-3xl mx-auto" data-id="element-171">
        <div ref={fadeIn.ref} className={fadeIn.className} data-id="element-172">
          <div className="text-center mb-6 md:mb-10" data-id="element-173">
            <h2 className="text-2xl md:text-5xl font-black text-gray-900 mb-3 md:mb-4" data-id="element-174">
              👀 Mirá lo que hay adentro
            </h2>
            <p className="text-sm md:text-lg text-gray-500 font-medium" data-id="element-175">
              Páginas reales del MegaPack Mi Espacio Interior
            </p>
          </div>

          <div className="relative" data-id="element-176">
            {/* Desktop arrows */}
            <button onClick={prev} className="hidden md:flex absolute -left-12 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white border border-gray-200 rounded-full items-center justify-center shadow-md hover:bg-gray-50 transition-colors cursor-pointer" aria-label="Anterior" data-id="element-177">
              <ChevronLeftIcon className="w-5 h-5 text-gray-700" data-id="element-178" />
            </button>
            <button onClick={next} className="hidden md:flex absolute -right-12 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white border border-gray-200 rounded-full items-center justify-center shadow-md hover:bg-gray-50 transition-colors cursor-pointer" aria-label="Siguiente" data-id="element-179">
              <ChevronRightIcon className="w-5 h-5 text-gray-700" data-id="element-180" />
            </button>

            {/* Carousel container */}
            <div ref={scrollRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4" style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }} data-id="element-181">
              {PREVIEW_PAGES.map((page, i) => <div key={i} className="flex-shrink-0 w-[85%] md:w-full snap-center" data-id="element-182">
                  <div className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-183">
                    <img src={page.src} alt={page.alt} className="w-full h-auto object-contain" loading="lazy" decoding="async" width={800} height={1000} sizes="(max-width: 768px) 85vw, 700px" data-id="element-184" />
                  </div>
                  <p className="text-center text-xs md:text-sm text-gray-400 font-medium mt-2" data-id="element-185">
                    {page.alt}
                  </p>
                </div>)}
            </div>

            {/* Dot indicators */}
            <div className="flex items-center justify-center gap-2 mt-4" data-id="element-186">
              {PREVIEW_PAGES.map((_, i) => <button key={i} onClick={() => goTo(i)} className={`w-2.5 h-2.5 rounded-full cursor-pointer ${i === current ? 'bg-blue-600 w-6' : 'bg-gray-300'}`} aria-label={`Ir a página ${i + 1}`} data-id="element-187" />)}
            </div>

            {/* Mobile swipe hint */}
            <p className="text-center text-[10px] text-gray-400 mt-2 md:hidden" data-id="element-188">
              ← Deslizá para ver más →
            </p>
          </div>
        </div>
      </div>
    </section>;
}